import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  alpha,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { ArrowBack, CheckCircle } from '@mui/icons-material';
import type { LegalAppearance } from './common/LegalAppearance';

interface PrivacyPolicyProps {
  onBack: () => void;
  language?: 'sv' | 'en';
  appearance?: LegalAppearance;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onBack, language = 'sv', appearance }) => {
  const brandColors = appearance || {
    primary: '#0EA5E9',
    secondary: '#22C55E',
    text: '#0F172A',
    textSecondary: '#475569',
    background: '#F8FAFC',
  };

  const content = {
    sv: {
      title: 'Integritetspolicy',
      subtitle: 'KlaraView',
      backButton: '← Tillbaka',
      intro: 'KlaraView behandlar personuppgifter i enlighet med EU:s dataskyddsförordning (GDPR) och tillämplig svensk lagstiftning.',
      purposeTitle: 'Vi behandlar endast personuppgifter som är nödvändiga för att:',
      purposes: [
        'tillhandahålla och säkra plattformens funktion',
        'administrera kundrelationer',
        'hantera B2B-affärsutveckling och direktmarknadsföring till relevanta yrkesroller',
        'uppfylla avtalsmässiga och rättsliga krav',
      ],
      sections: [
        'Kundernas fastighets- och bilddata som analyseras inom KlaraView används uteslutande för det avtalade uppdragets ändamål och behandlas konfidentiellt.',
        'Inspektionsdata lagras och behandlas inom EU/EES. Åtkomst till data är strikt behörighetsstyrd.',
        'KlaraView delar inte kunddata med tredje part i marknadsföringssyfte.',
        'Behandling för B2B-affärsutveckling och kommunikation med relevanta yrkesroller sker med stöd av berättigat intresse. Detta omfattar direktmarknadsföring av KlaraViews tjänster.',
        'Du har rätt att när som helst invända mot behandling av dina personuppgifter för direktmarknadsföring genom att kontakta info@klaraview.se eller svara på vårt meddelande. Vi upphör då med behandling för detta ändamål.',
        'Registrerade har rätt att begära information om, rättelse av eller radering av personuppgifter i enlighet med GDPR.',
      ],
      contactTitle: 'För frågor kring dataskydd eller informationssäkerhet:',
      contactEmail: 'info@klaraview.se',
      updated: 'Senast uppdaterad: September 2026',
    },
    en: {
      title: 'Privacy Policy',
      subtitle: 'KlaraView',
      backButton: '← Back',
      intro: 'KlaraView processes personal data in accordance with the EU General Data Protection Regulation (GDPR) and applicable Swedish legislation.',
      purposeTitle: 'We only process personal data necessary to:',
      purposes: [
        'provide and secure the platform\'s functionality',
        'administer customer relationships',
        'manage B2B business development and direct marketing to relevant professional roles',
        'fulfill contractual and legal requirements',
      ],
      sections: [
        'Customer property and image data analyzed within KlaraView is used exclusively for the agreed assignment purposes and treated confidentially.',
        'Inspection data is stored and processed within the EU/EEA. Access to data is strictly permission-controlled.',
        'KlaraView does not share customer data with third parties for marketing purposes.',
        'Processing for B2B business development and communication with relevant professional roles is based on legitimate interest. This includes direct marketing of KlaraView services.',
        'You have the right to object at any time to the processing of your personal data for direct marketing by contacting info@klaraview.se or replying to our message. We will then stop processing for this purpose.',
        'Data subjects have the right to request information about, correction of, or deletion of personal data in accordance with GDPR.',
      ],
      contactTitle: 'For questions regarding data protection or information security:',
      contactEmail: 'info@klaraview.se',
      updated: 'Last updated: September 2026',
    },
  };

  const t = content[language];

  return (
    <Box sx={{ bgcolor: brandColors.background, minHeight: '100vh', py: { xs: 4, md: 8 } }}>
      <Container maxWidth="md">
        <Button
          onClick={onBack}
          startIcon={<ArrowBack />}
          sx={{
            mb: 4,
            color: brandColors.primary,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {t.backButton}
        </Button>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: appearance ? 0 : 3,
            border: `1px solid ${alpha(brandColors.text, 0.08)}`,
          }}
        >
          <Box sx={{ mb: 4 }}>
            {appearance ? <Box sx={{ mb: 3 }}>{appearance.logo}</Box> : <img
              src="/klaraview-logo-old.svg"
              alt="KlaraView"
              style={{ width: '160px', height: 'auto', marginBottom: '16px' }}
            />}
            <Typography
              variant="h3"
              component={appearance ? 'h1' : 'h3'}
              sx={{
                fontWeight: 700,
                color: brandColors.text,
                fontSize: { xs: '1.8rem', md: '2.5rem' },
                mb: 1,
              }}
            >
              {t.title}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{ color: brandColors.textSecondary }}
            >
              {t.subtitle}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography
              variant="body1"
              sx={{
                color: brandColors.textSecondary,
                lineHeight: 1.8,
                fontSize: '1.05rem',
              }}
            >
              {t.intro}
            </Typography>

            <Box>
              <Typography
                variant="body1"
                sx={{
                  color: brandColors.text,
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                {t.purposeTitle}
              </Typography>
              <List dense>
                {t.purposes.map((purpose, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircle sx={{ color: brandColors.secondary, fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={purpose}
                      primaryTypographyProps={{
                        sx: { color: brandColors.textSecondary, fontSize: '1rem' },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            {t.sections.map((section, index) => (
              <Typography
                key={index}
                variant="body1"
                sx={{
                  color: brandColors.textSecondary,
                  lineHeight: 1.8,
                  fontSize: '1.05rem',
                }}
              >
                {section}
              </Typography>
            ))}

            <Box
              sx={{
                mt: 2,
                p: 3,
                borderRadius: 2,
                bgcolor: alpha(brandColors.primary, 0.05),
                border: `1px solid ${alpha(brandColors.primary, 0.15)}`,
              }}
            >
              <Typography
                variant="body1"
                sx={{ color: brandColors.text, fontWeight: 500, mb: 1 }}
              >
                {t.contactTitle}
              </Typography>
              <Typography
                component="a"
                href="mailto:info@klaraview.se"
                variant="body1"
                sx={{
                  color: brandColors.primary,
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {t.contactEmail}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 6, pt: 4, borderTop: `1px solid ${alpha(brandColors.text, 0.08)}` }}>
            <Typography variant="body2" sx={{ color: brandColors.textSecondary }}>
              {t.updated}
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
